/**
 * O que já saiu para o mundo, dito ANTES de mexer na fatura.
 * Prévia: https://claude.ai/artifact/LGx85pETYBgDmYz8zmmaFP
 *
 * Até 03/10/2026 o editor não tinha nenhuma menção a nota fiscal, boleto,
 * autorizada ou registrado. Dava para abrir uma fatura com NFS-e autorizada e
 * boleto confirmado, mudar o valor de um item, salvar, e a tela não dizia nada:
 * a nota ficava com o valor velho e o boleto no banco também.
 *
 * NÃO bloqueia: às vezes é preciso corrigir mesmo. Avisa o que não se conserta
 * sozinho (nota precisa ser regerada, boleto baixado e reemitido).
 */

export interface NotaDaFatura {
  type: string;
  documentNumber: string | null;
  fiscalStatus?: string;
}

export interface AvisoDeEdicao {
  notas: string[];
  boleto: string | null;
  pago: number;
  /** A frase do aviso, pronta. */
  texto: string;
}

const juntar = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

/** Boleto que ainda está no banco valendo o valor antigo. */
const BOLETO_VIVO: Record<string, string> = { registrado: 'registrado', confirmado: 'confirmado' };

/** O que da fatura já saiu para o mundo e não volta sozinho: notas autorizadas e boleto vivo no banco. Também usado
 *  pelos confirms de cancelar e reabrir (utils/confirmacaoDeSaida). */
export function saidoParaOMundo(
  inv: { nossoNumero?: string | null; boletoStatus?: string | null } | null | undefined,
  notas: NotaDaFatura[] | null | undefined,
): { notas: string[]; boleto: string | null } {
  const autorizadas = (notas || [])
    .filter((n) => (n.fiscalStatus || 'Autorizada') === 'Autorizada')
    .map((n) => `${String(n.type || '').toUpperCase() === 'NFCOM' ? 'NFCom' : 'NFS-e'} ${n.documentNumber || 's/nº'}`);
  const st = String(inv?.boletoStatus || '').toLowerCase();
  const boleto = inv?.nossoNumero && BOLETO_VIVO[st] ? `boleto ${BOLETO_VIVO[st]} (nº ${inv.nossoNumero})` : null;
  return { notas: autorizadas, boleto };
}

export function avisoDeEdicao(
  inv: { nossoNumero?: string | null; boletoStatus?: string | null; paidValue?: number | null } | null | undefined,
  notas: NotaDaFatura[] | null | undefined,
  brl: (v: number) => string,
): AvisoDeEdicao | null {
  if (!inv) return null;
  const { notas: autorizadas, boleto } = saidoParaOMundo(inv, notas);
  const pago = Number(inv.paidValue) || 0;
  if (!autorizadas.length && !boleto && pago <= 0) return null;

  const tem: string[] = [];
  if (autorizadas.length) tem.push(`${juntar(autorizadas)} ${autorizadas.length === 1 ? 'autorizada' : 'autorizadas'}`);
  if (boleto) tem.push(boleto);
  if (pago > 0) tem.push(`R$ ${brl(pago)} já pago`);

  const consertos: string[] = [];
  if (autorizadas.length) consertos.push(autorizadas.length === 1 ? 'a nota precisa ser regerada' : 'as notas precisam ser regeradas');
  if (boleto) consertos.push('o boleto, baixado e reemitido');
  if (pago > 0) consertos.push('o pagamento registrado não se ajusta');

  const texto =
    `Esta fatura já tem ${juntar(tem)}. Mudar valor, item ou período não altera ${tem.length === 1 ? 'isso' : 'nenhum deles'}: ` +
    `${juntar(consertos)}.`;
  return { notas: autorizadas, boleto, pago, texto };
}
